import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine } from "@/components/MaskedLine";
import RadarTargets from "@/components/RadarTargets";

const HEADLINE = [
  { text: "SPECIAL MISSION", accent: false },
  { text: "ELECTRONICS", accent: false },
  { text: "INTEGRATION", accent: true },
];

const Cross = ({ className }) => (
  <span aria-hidden="true" className={`pointer-events-none absolute select-none text-sm text-orange-500/40 ${className}`}>
    +
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 280]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="bg-blueprint relative overflow-hidden">
      <motion.div
        style={{ y: glowY }}
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[4%] h-[55vh] w-[90vw] -translate-x-1/2 rounded-full bg-orange-500/10 blur-[130px]"
      />
      <Cross className="left-[6%] top-[22%]" />
      <Cross className="right-[8%] top-[30%]" />
      <Cross className="bottom-[26%] left-[10%]" />
      <Cross className="bottom-[20%] right-[6%]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col items-center justify-center px-5 pb-28 pt-32 sm:px-8"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-8 text-[10px] tracking-[0.45em] text-orange-500/90 sm:text-xs"
          data-testid="hero-eyebrow"
        >
          {"// AIRBORNE · LAND · MARITIME //"}
        </motion.p>

        <motion.div
          style={{ y: logoY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div
            aria-hidden="true"
            data-testid="hero-radar-sweep"
            className="pointer-events-none absolute left-1/2 top-1/2 aspect-square w-[112%] -translate-x-1/2 -translate-y-1/2"
          >
            <div className="absolute inset-0 rounded-full border border-orange-500/15" />
            <div className="absolute inset-[18%] rounded-full border border-orange-500/10" />
            <div className="absolute inset-[36%] rounded-full border border-orange-500/10" />
            <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-orange-500/10" />
            <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-orange-500/10" />
            <div className="radar-sweep absolute inset-0 rounded-full" />
            <span className="animate-blink absolute left-[30%] top-[37%] h-1 w-1 rounded-full bg-orange-500 drop-shadow-[0_0_6px_rgba(249,115,22,0.9)]" />
            <span className="animate-blink absolute left-[69%] top-[60%] h-1 w-1 rounded-full bg-orange-500 drop-shadow-[0_0_6px_rgba(249,115,22,0.9)] [animation-delay:0.9s]" />
            <RadarTargets />
          </div>
          <img
            src="/assets/kbxdu-logo-dark.png"
            alt="KB/XDU — Special Mission Electronics Integration"
            data-testid="hero-logo"
            className="w-[min(1500px,96vw)] mix-blend-screen drop-shadow-[0_0_45px_rgba(249,115,22,0.22)]"
          />
        </motion.div>

        <h1
          data-testid="hero-heading"
          className="mt-12 text-center font-display text-[clamp(2.6rem,8.5vw,6.25rem)] font-bold uppercase leading-[0.92] tracking-tight"
        >
          {HEADLINE.map((line, i) => (
            <MaskedLine key={line.text} delay={0.4 + i * 0.13}>
              <span className={line.accent ? "text-stroke-orange" : "text-stone-100"}>{line.text}</span>
            </MaskedLine>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="mt-9 max-w-xl text-center text-xs leading-relaxed text-stone-400 sm:text-sm"
          data-testid="hero-subtext"
        >
          KB/XDU designs, integrates and sustains mission-critical electronics for special mission
          platforms — ISR, tactical communications, electronic warfare and mission computing,
          delivered as one coherent system.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-11 flex flex-col items-center gap-4 sm:flex-row"
        >
          <a
            href="#capabilities"
            data-testid="hero-cta-capabilities"
            className="border border-orange-500 bg-orange-500 px-7 py-3 font-display text-sm font-semibold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-transparent hover:text-orange-500"
          >
            Explore Capabilities
          </a>
          <a
            href="#contact"
            data-testid="hero-cta-contact"
            className="border border-stone-700 px-7 py-3 font-display text-sm font-semibold uppercase tracking-[0.2em] text-stone-300 transition-colors duration-300 hover:border-orange-500 hover:text-orange-500"
          >
            Establish Uplink
          </a>
        </motion.div>
      </motion.div>

      <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between border-t border-orange-500/15 px-5 py-3 text-[9px] tracking-[0.3em] text-stone-500 sm:px-8 sm:text-[10px]">
        <span data-testid="hero-coords">34.0522°N / 118.2437°W</span>
        <span className="hidden sm:inline" data-testid="hero-uplink-status">UPLINK: SECURE</span>
        <span data-testid="hero-scroll-cue">SCROLL // 001</span>
      </div>
    </section>
  );
}
